import {
  Directive,
  ElementRef,
  input,
  OnDestroy,
  Renderer2,
  inject,
  HostListener,
  effect,
  numberAttribute,
  booleanAttribute,
} from '@angular/core';
import { Overlay, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { MibTooltipComponent } from './tooltip.component';

export type TooltipPosition = 'above' | 'below' | 'left' | 'right';

@Directive({
  selector: '[mibTooltip]',
  exportAs: 'mibTooltip',
})
export class MibTooltipDirective implements OnDestroy {
  private overlay = inject(Overlay);
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);

  private overlayRef: OverlayRef | null = null;
  private tooltipInstance: MibTooltipComponent | null = null;
  private showTimeout: ReturnType<typeof setTimeout> | null = null;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  readonly mibTooltip = input('');
  readonly mibTooltipPosition = input<TooltipPosition>('below');
  readonly mibTooltipShowDelay = input(0, { transform: numberAttribute });
  readonly mibTooltipHideDelay = input(0, { transform: numberAttribute });
  readonly mibTooltipDisabled = input(false, { transform: booleanAttribute });
  readonly mibTooltipKeepOpen = input(false, { transform: booleanAttribute });

  constructor() {
    // Watch for tooltip message changes
    effect(() => {
      const message = this.mibTooltip();
      if (this.tooltipInstance && this.overlayRef?.hasAttached()) {
        this.tooltipInstance.show(message);
      }
    });
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.mibTooltipDisabled()) {
      return;
    }

    // Cancel any pending hide operation
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    const showDelay = this.mibTooltipShowDelay();
    if (showDelay > 0) {
      this.showTimeout = setTimeout(() => this.show(), showDelay);
    } else {
      this.show();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    // Cancel any pending show operation
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }

    const hideDelay = this.mibTooltipHideDelay();
    if (hideDelay > 0) {
      this.hideTimeout = setTimeout(() => this.hide(), hideDelay);
    } else {
      this.hide();
    }
  }

  @HostListener('click')
  onClick(): void {
    if (!this.mibTooltipKeepOpen()) {
      this.hide();
    }
  }

  show(): void {
    if (!this.mibTooltip() || this.mibTooltipDisabled()) {
      return;
    }

    // Clear the show timeout since we're now showing
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }

    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    if (!this.overlayRef) {
      const positionStrategy = this.getPositionStrategy();
      this.overlayRef = this.overlay.create({
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
      });
    }

    if (!this.overlayRef.hasAttached()) {
      const portal = new ComponentPortal(MibTooltipComponent);
      const componentRef = this.overlayRef.attach(portal);
      this.tooltipInstance = componentRef.instance;
      this.tooltipInstance.show(this.mibTooltip());

      // Trigger opacity animation after a brief delay
      setTimeout(() => {
        const tooltipElement = this.overlayRef?.overlayElement.querySelector('mib-tooltip');
        if (tooltipElement) {
          this.renderer.setStyle(tooltipElement, 'opacity', '1');
        }
      }, 0);
    }
  }

  hide(): void {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      const tooltipElement = this.overlayRef.overlayElement.querySelector('mib-tooltip');
      if (tooltipElement) {
        this.renderer.setStyle(tooltipElement, 'opacity', '0');
      }

      // Wait for animation to complete before detaching
      setTimeout(() => {
        this.overlayRef?.detach();
        this.tooltipInstance = null;
      }, 200);
    }
  }

  private getPositionStrategy(): PositionStrategy {
    const position = this.mibTooltipPosition();
    const offset = 8;
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withFlexibleDimensions(true)
      .withViewportMargin(8)
      .withPush(true);

    switch (position) {
      case 'above':
        // Tooltip centered above parent, bottom of tooltip to top of parent with offset
        positionStrategy.withPositions([
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: -offset,
          },
        ]);
        break;
      case 'below':
        // Tooltip centered below parent, top of tooltip to bottom of parent with offset
        positionStrategy.withPositions([
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: offset,
          },
        ]);
        break;
      case 'left':
        // Tooltip vertically centered on left, right of tooltip to left of parent with offset
        positionStrategy.withPositions([
          {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center',
            offsetX: -offset,
          },
        ]);
        break;
      case 'right':
        // Tooltip vertically centered on right, left of tooltip to right of parent with offset
        positionStrategy.withPositions([
          {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
            offsetX: offset,
          },
        ]);
        break;
    }

    return positionStrategy;
  }

  ngOnDestroy(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }
}
